/**
 * App shell adapted from shadcn/ui's `dashboard-01` block (new-york v4).
 *
 * Three sidebar groups:
 *   1. Hauptmenü      — Übersicht (root), Kalender, Tickets
 *   2. Einstellungen  — Termin-Typen, Telefon, Anrufe-Log, Ticket-System
 *   3. Verwaltung     — Benutzer, Mitgliedschafts-Pläne (admin only)
 *
 * No Quick-Create button, no NavSecondary block. Header shows a single
 * page-title (h1) with a ghost action button on the right.
 */
import * as React from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
// Brand logos — `logo_light` is the dark-teal artwork shown on the
// light theme, `logo_dark` is the white artwork shown on the dark
// theme. The AppShell sidebar header below switches between them via
// Tailwind's `dark:` variant. If you ever update the logo, drop the
// new PNGs at the same paths — Vite hashes the filename automatically.
import logoLight from '../assets/logo_light.png';
import logoDark from '../assets/logo_dark.png';
import { signOut } from 'firebase/auth';
import {
  CalendarClock,
  Calendar,
  ChevronsUpDown,
  Coins,
  Code2,
  Home,
  Inbox,
  KeyRound,
  LogOut,
  Moon,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import { useTheme } from '../theme/ThemeProvider';
import { auth } from '../lib/firebase';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useMyAlerts } from '../hooks/useBilling';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { HeaderActionsLeftSlot, HeaderActionsSlot } from './HeaderActions';
import { ProfileDialog } from './ProfileDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from './ui/sidebar';

interface NavEntry {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When set, the row is "active" for this prefix (covers sub-routes
   *  like /settings/phone or /settings/calls/<id>). */
  matchPrefix?: string;
}

interface NavGroup {
  label?: string;
  items: ReadonlyArray<NavEntry>;
}

/**
 * Sidebar groups are computed per-render so they can react to the
 * caller's role. Non-admins see Einstellungen + their own Nutzung & Kosten
 * under "Verwaltung"; admins see the admin overview at the same label,
 * plus Benutzer + Mitgliedschafts-Pläne.
 */
function getNavGroups(isAdmin: boolean): ReadonlyArray<NavGroup> {
  return [
    {
      items: [
        { to: '/', label: 'Übersicht', icon: Home },
        { to: '/calendar', label: 'Kalender', icon: Calendar, matchPrefix: '/calendar' },
        { to: '/tickets', label: 'Anfragen', icon: Inbox },
      ],
    },
    {
      label: 'Mein Bereich',
      items: isAdmin
        ? [
            { to: '/settings', label: 'Einstellungen', icon: Settings, matchPrefix: '/settings' },
            { to: '/admin/users', label: 'Benutzer', icon: Users, matchPrefix: '/admin/users' },
            { to: '/admin/dev', label: 'Entwicklereinstellungen', icon: Code2, matchPrefix: '/admin/dev' },
          ]
        : [
            { to: '/settings', label: 'Einstellungen', icon: Settings, matchPrefix: '/settings' },
            { to: '/usage', label: 'Nutzung & Kosten', icon: Coins, matchPrefix: '/usage' },
          ],
    },
  ];
}

const PAGE_TITLES: Array<{ test: (p: string) => boolean; title: string }> = [
  { test: (p) => p === '/', title: 'Übersicht' },
  { test: (p) => p === '/tickets', title: 'Anfragen' },
  { test: (p) => p === '/calendar' || p.startsWith('/calendar/'), title: 'Kalender' },
  // Single header for the whole settings tree. Sub-Tab erscheint in der
  // Tab-Strip-Leiste innerhalb von SettingsLayout, hier kein doppeltes
  // Label nötig.
  { test: (p) => p === '/settings' || p.startsWith('/settings/'), title: 'Einstellungen' },
  { test: (p) => p === '/usage', title: 'Nutzung & Kosten' },
  { test: (p) => p === '/admin/users', title: 'Benutzer' },
  { test: (p) => p.startsWith('/admin/users/'), title: 'Benutzer · Detail' },
  { test: (p) => p.startsWith('/admin/dev'), title: 'Entwicklereinstellungen' },
  { test: (p) => p === '/billing/return', title: 'Zahlung wird bestätigt' },
  { test: (p) => p === '/account', title: 'Mein Account' },
];

const TIER_LABEL: Record<string, string> = {
  trial: 'Trial',
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
  ultimate: 'Ultimate',
};

export function AppShell() {
  usePrefetchHeavyData();
  return (
    <SidebarProvider defaultOpen>
      <CP3Sidebar />
      <SidebarInset>
        <SiteHeader />
        <ShellOutletFrame />
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * Pre-fetches the data sets that the user is most likely to navigate
 * to next (Calendar mirror, Tickets list) so those screens render
 * instantly. Cache hits keep the server cost low; misses just warm up
 * in the background and don't block the UI.
 *
 * Privacy-aware: we only prefetch the user's own data using the
 * existing callables — nothing is mirrored or cached anywhere outside
 * React Query's in-memory store, so a sign-out clears everything.
 */
function usePrefetchHeavyData(): void {
  const { user } = useUser();
  const qc = useQueryClient();
  const uid = user?.uid;
  React.useEffect(() => {
    if (!uid) return;
    // Calendar — wide window matching CalendarPage's default cursor
    // (-1 month .. +2 months around today). Same query key shape, so
    // CalendarPage hits the cache directly when the user navigates.
    const now = new Date();
    const fromMs = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
    const toMs = new Date(now.getFullYear(), now.getMonth() + 3, 0).getTime();
    qc.prefetchQuery({
      queryKey: ['mirror', fromMs, toMs] as const,
      staleTime: 60_000,
      queryFn: async () => {
        const fn = httpsCallable<
          { fromMs?: number; toMs?: number },
          { items: unknown[] }
        >(functions, 'listMirrorEvents');
        const r = await fn({ fromMs, toMs });
        return r.data.items;
      },
    });

    // Tickets — match TicketsPage's default filter (status=active = [open,
    // in-progress, waiting-customer]). React Query's hashKey is stable
    // for object literals so the page hits this cache entry on first paint.
    const ticketsDefaultFilter = {
      status: ['open', 'in-progress', 'waiting-customer'],
    };
    qc.prefetchQuery({
      queryKey: ['tickets', 'list', ticketsDefaultFilter] as const,
      staleTime: 30_000,
      queryFn: async () => {
        const fn = httpsCallable<typeof ticketsDefaultFilter, { items: unknown[] }>(
          functions,
          'listTickets',
        );
        const r = await fn(ticketsDefaultFilter);
        return r.data.items;
      },
    });
  }, [uid, qc]);
}

function CP3Sidebar() {
  const { isAdmin } = useUser();
  const groups = getNavGroups(isAdmin);

  return (
    <Sidebar collapsible="offcanvas" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="hover:bg-transparent">
              <Link
                to="/"
                className="flex items-center justify-start p-1.5"
                aria-label="griassdy — Startseite"
              >
                {/* Two logo variants imported via Vite — the bundler
                    fingerprints them and serves cache-able URLs. We
                    toggle visibility with Tailwind's `dark:` variant
                    instead of switching `src` at runtime so there's no
                    flicker on theme change and the inactive variant
                    isn't fetched. */}
                <img
                  src={logoLight}
                  alt=""
                  className="h-12 w-auto select-none dark:hidden"
                  draggable={false}
                />
                <img
                  src={logoDark}
                  alt=""
                  className="hidden h-12 w-auto select-none dark:block"
                  draggable={false}
                />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group, idx) => (
          <SidebarGroup key={idx}>
            {group.label && (
              <div className="px-2 pb-1 pt-2 text-xs font-medium text-sidebar-foreground/60">
                {group.label}
              </div>
            )}
            <SidebarMenu>
              {group.items.map((entry) => (
                <NavRow key={entry.to} entry={entry} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

function NavRow({ entry }: { entry: NavEntry }) {
  const { icon: Icon, to, label, matchPrefix } = entry;
  const { pathname } = useLocation();
  const prefixActive = matchPrefix
    ? pathname === matchPrefix || pathname.startsWith(`${matchPrefix}/`)
    : null;
  // Only the "Nutzung & Kosten" row carries an alert badge today (e.g. for
  // expired payment methods). We pull from useMyAlerts which itself dedupes
  // via React Query so this is cheap.
  const alerts = useMyAlerts();
  const showBadge = to === '/usage' && alerts.total > 0;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={label}>
        <NavLink to={to} end={to === '/' && !matchPrefix}>
          {({ isActive }) => {
            const active = prefixActive ?? isActive;
            return (
              <span
                data-active={active ? 'true' : undefined}
                className={[
                  'flex w-full items-center gap-2',
                  active ? 'font-medium text-sidebar-accent-foreground' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <Icon className="size-4" />
                <span className="flex-1">{label}</span>
                {showBadge && (
                  <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold leading-none text-destructive-foreground">
                    {alerts.total}
                  </span>
                )}
              </span>
            );
          }}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function NavUser() {
  const { user, profile } = useUser();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  // Prefer the structured firstName / lastName combo when available so
  // the sidebar greets the user the same way the dashboard does. Falls
  // back through displayName, then the email's local-part, then "Konto".
  const fullName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim() ||
    profile?.displayName?.trim() ||
    user?.email?.split('@')[0] ||
    '';
  const displayName = fullName || 'Konto';
  const initials = getInitials(fullName, user?.email ?? '');
  const [profileOpen, setProfileOpen] = React.useState(false);

  async function onSignOut() {
    try {
      await signOut(auth);
    } finally {
      navigate('/signin', { replace: true });
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg bg-primary/15 text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-semibold">{displayName}</span>
                <span className="truncate text-[11px] text-muted-foreground">{user?.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="end"
            sideOffset={8}
            className="min-w-60 rounded-lg"
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg bg-primary/15 text-primary">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-sm font-semibold">{displayName}</span>
                  <span className="truncate text-[11px] text-muted-foreground">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <MembershipBlock />
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setProfileOpen(true)}>
              <Users className="mr-2 size-4" /> Meine Daten
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/account')}>
              <KeyRound className="mr-2 size-4" /> Mein Account
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/settings/provider')}>
              <Settings className="mr-2 size-4" /> Einstellungen
            </DropdownMenuItem>
            {/* Theme toggle — keep the menu open on click so the user
                can see the theme flip without losing their place. */}
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                toggleTheme();
              }}
              className="cursor-pointer"
            >
              {theme === 'dark' ? (
                <Moon className="mr-2 size-4" />
              ) : (
                <Sun className="mr-2 size-4" />
              )}
              <span className="flex-1">Erscheinungsbild</span>
              <span className="mr-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                {theme === 'dark' ? 'Dunkel' : 'Hell'}
              </span>
              <Switch
                checked={theme === 'light'}
                onCheckedChange={() => toggleTheme()}
                aria-label="Light Mode umschalten"
                onClick={(e) => e.stopPropagation()}
              />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void onSignOut()}>
              <LogOut className="mr-2 size-4" /> Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      {profileOpen && <ProfileDialog onClose={() => setProfileOpen(false)} />}
    </SidebarMenu>
  );
}

/**
 * Wraps `<Outlet/>` and decides whether the active route gets the
 * default padded card-grid frame (most pages) or a flush, edge-to-edge
 * frame with no gap to the SiteHeader (Kalender + Tickets — they
 * provide their own internal scrolling so the surrounding shell would
 * just be wasted space).
 */
function ShellOutletFrame() {
  const { pathname } = useLocation();
  const flush =
    pathname === '/calendar'
    || pathname.startsWith('/calendar/')
    || pathname === '/tickets';
  if (flush) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        <Outlet />
      </div>
    </div>
  );
}

function SiteHeader() {
  const { pathname } = useLocation();
  const { profile, user } = useUser();

  // Special-case the dashboard root: greet the user by first name. Falls
  // back to displayName, then e-mail prefix.
  let title = PAGE_TITLES.find((m) => m.test(pathname))?.title ?? '';
  if (pathname === '/') {
    const first =
      profile?.firstName?.trim() ||
      (profile?.displayName ?? '').trim().split(/\s+/)[0] ||
      '';
    const fallback = user?.email?.split('@')[0];
    const name = first || fallback;
    title = name ? `Schön, dass du da bist, ${name}` : 'Mein Dashboard';
  }

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/60">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mx-2 h-4" />
        <h1 className="text-base font-medium whitespace-nowrap">{title}</h1>
        <HeaderActionsLeftSlot />
        <HeaderActionsSlot />
      </div>
    </header>
  );
}

/**
 * Inline membership block inside the NavUser dropdown — replaces the
 * old prominent "Trial" card on the dashboard. Shows tier + status and
 * — when applicable — the days left.
 */
function MembershipBlock() {
  const { profile, isAdmin } = useUser();
  const m = profile?.membership;
  if (!m && !isAdmin) {
    return (
      <div className="px-2 py-1.5 text-xs text-muted-foreground">
        Keine Mitgliedschaft hinterlegt
      </div>
    );
  }
  const validUntil = readDate(m?.validUntil ?? null);
  const daysLeft = validUntil
    ? Math.ceil((validUntil.getTime() - Date.now()) / 86_400_000)
    : null;
  const expired = daysLeft != null && daysLeft <= 0;
  const expiringSoon = daysLeft != null && daysLeft > 0 && daysLeft <= 14;
  return (
    <div className="space-y-1.5 px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {m?.tier && (
          <Badge variant="default">{TIER_LABEL[m.tier] ?? m.tier}</Badge>
        )}
        {m?.status && (
          <Badge
            variant={
              m.status === 'active'
                ? 'success'
                : m.status === 'paused'
                  ? 'warning'
                  : 'destructive'
            }
          >
            {m.status}
          </Badge>
        )}
        {isAdmin && <Badge variant="warning">Admin</Badge>}
      </div>
      {validUntil && (
        <div
          className={[
            'flex items-center gap-1 text-[11px]',
            expired ? 'text-destructive' : expiringSoon ? 'text-amber-300' : 'text-muted-foreground',
          ].join(' ')}
        >
          <CalendarClock className="size-3" />
          {expired ? 'Abgelaufen ' : 'Noch '}
          {expired ? null : `${daysLeft} Tag${daysLeft === 1 ? '' : 'e'} · `}
          bis {validUntil.toLocaleDateString('de-DE')}
        </div>
      )}
      {!validUntil && m && (
        <div className="text-[11px] text-muted-foreground">Unbegrenzt gültig</div>
      )}
    </div>
  );
}

function readDate(v: unknown): Date | null {
  if (!v) return null;
  const obj = v as { toDate?: () => Date; _seconds?: number; seconds?: number };
  if (obj.toDate) return obj.toDate();
  const seconds = obj._seconds ?? obj.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  if (v instanceof Date) return v;
  return null;
}

function getInitials(name: string, email: string): string {
  const trimmed = name.trim();
  if (trimmed) {
    const parts = trimmed.split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || trimmed[0]!.toUpperCase();
  }
  return (email[0] ?? '?').toUpperCase();
}
